// src/components/events/EventDetailPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Route,
  IndianRupee,
  Building2,
  Clock,
  Flag,
  Navigation,
  Milestone,
  Bike,
  Share2,
  Heart,
} from "lucide-react";
import { fetchEventById, clearSelectedEvent } from "@/store/slices/eventsSlice";
import { getImageUrl } from "@/utils/imageUrl";
import Modal from "../common/Modal";
import EventRegistrationForm from "./EventRegistrationForm";

/* ── helpers ─────────────────────────────────────────── */

const EVENT_TYPE_CONFIG = {
  cycle_tour: {
    label: "Cycle Tour",
    color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    icon: "🚴",
  },
  cycle_kumbh: {
    label: "Cycle Kumbh",
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    icon: "🎪",
  },
  flash_mob: {
    label: "Flash Mob",
    color: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300",
    icon: "⚡",
  },
  koset_ride: {
    label: "Koset Ride",
    color: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    icon: "🏁",
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ── Skeleton ────────────────────────────────────────── */
const DetailSkeleton = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950 animate-pulse">
    <div className="h-72 bg-gray-200 dark:bg-gray-800" />
    <div className="container mx-auto px-6 -mt-16">
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="space-y-3">
          <div className="h-4 bg-gray-100 dark:bg-gray-700/50 rounded w-full" />
          <div className="h-4 bg-gray-100 dark:bg-gray-700/50 rounded w-full" />
          <div className="h-4 bg-gray-100 dark:bg-gray-700/50 rounded w-3/4" />
        </div>
      </div>
    </div>
  </div>
);

/* ── Info Card (reusable) ────────────────────────────── */
const InfoItem = ({ icon: Icon, label, value, iconColor = "text-green-500" }) => (
  <div className="flex items-start gap-3">
    <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 shrink-0`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">
        {label}
      </p>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">
        {value}
      </p>
    </div>
  </div>
);

/* ── Main Component ──────────────────────────────────── */
const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedEvent: event, isEventLoading, eventError } = useSelector(
    (state) => state.events
  );

  const [showRegModal, setShowRegModal] = useState(false);

  useEffect(() => {
    dispatch(fetchEventById(id));
    return () => dispatch(clearSelectedEvent());
  }, [dispatch, id]);

  const typeConfig = EVENT_TYPE_CONFIG[event?.event_type] || {
    label: event?.event_type,
    color: "bg-gray-100 text-gray-700",
    icon: "🚲",
  };

  const locationStr = [
    event?.location?.address,
    event?.location?.city,
    event?.location?.state_code,
  ]
    .filter(Boolean)
    .join(", ");

  // Loading
  if (isEventLoading) return <DetailSkeleton />;

  // Error
  if (eventError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md px-6"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <Flag className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Event Not Found
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{eventError}</p>
          <button
            onClick={() => navigate("/events")}
            className="px-6 py-2.5 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 transition-colors"
          >
            Back to Events
          </button>
        </motion.div>
      </div>
    );
  }

  // No event loaded yet
  if (!event) return <DetailSkeleton />;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors pb-12">
      {/* ── Hero Banner ── */}
      <div className="relative h-64 md:h-80 overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 dark:from-green-800 dark:via-emerald-800 dark:to-teal-900">
        {/* Animated BG */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
            transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
            className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
            transition={{ repeat: Infinity, duration: 15, ease: "easeInOut" }}
            className="absolute -bottom-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"
          />
        </div>

        {/* Dot pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full">
            <defs>
              <pattern id="hero-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-dots)" />
          </svg>
        </div>

        {/* Back button */}
        <div className="absolute top-6 left-6 z-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            All Events
          </button>
        </div>

        {/* Hero content */}
        <div className="absolute bottom-0 inset-x-0 pb-8 pt-16 bg-gradient-to-t from-black/50 to-transparent">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${typeConfig.color}`}>
                  <span>{typeConfig.icon}</span>
                  {typeConfig.label}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-white/20 backdrop-blur-sm text-white">
                  {event.is_paid ? (
                    <>
                      <IndianRupee className="w-3 h-3" />
                      {event.entry_fee}
                    </>
                  ) : (
                    "FREE"
                  )}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-2">
                {event.title}
              </h1>
              {event.organizer_id?.organization_name && (
                <div className="flex items-center gap-2 text-green-200/80">
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Organized by {event.organizer_id.organization_name}
                  </span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="container mx-auto px-6 -mt-6 relative z-10">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column — Main info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description card */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-md border border-gray-100 dark:border-gray-800"
            >
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Bike className="w-5 h-5 text-green-500" />
                About This Event
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {event.description}
              </p>
            </motion.div>

            {/* Route info card */}
            {event.event_route && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-md border border-gray-100 dark:border-gray-800"
              >
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                  <Route className="w-5 h-5 text-blue-500" />
                  Route Information
                </h2>

                <div className="grid sm:grid-cols-2 gap-5">
                  {event.event_route.name && (
                    <InfoItem
                      icon={Flag}
                      label="Route Name"
                      value={event.event_route.name}
                      iconColor="text-blue-500"
                    />
                  )}
                  {event.event_route.distance > 0 && (
                    <InfoItem
                      icon={Navigation}
                      label="Distance"
                      value={`${event.event_route.distance.toFixed(1)} km`}
                      iconColor="text-indigo-500"
                    />
                  )}
                  {event.event_route.start_point && (
                    <InfoItem
                      icon={Flag}
                      label="Start Point"
                      value={event.event_route.start_point}
                      iconColor="text-green-500"
                    />
                  )}
                  {event.event_route.end_point && (
                    <InfoItem
                      icon={Milestone}
                      label="End Point"
                      value={event.event_route.end_point}
                      iconColor="text-red-500"
                    />
                  )}
                </div>

                {/* Landmarks */}
                {event.event_route.landmarks?.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-5 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-500" />
                      Route Landmarks
                    </h3>
                    <div className="relative pl-3 space-y-5 before:absolute before:inset-y-0 before:left-[19px] before:w-[2px] before:bg-gradient-to-b before:from-green-100 before:via-gray-100 before:to-gray-100 dark:before:from-green-900/30 dark:before:via-gray-800 dark:before:to-gray-800 pointer-events-none">
                      {event.event_route.landmarks.map((lm, i) => (
                        <div key={i} className="relative flex items-start gap-4 pointer-events-auto group">
                          {/* Timeline Node */}
                          <div className="relative z-10 w-[14px] h-[14px] rounded-full bg-white dark:bg-gray-900 border-[3px] border-green-400 dark:border-green-500 shadow-[0_0_0_4px_white] dark:shadow-[0_0_0_4px_#111827] mt-[7px] shrink-0" />
                          
                          {/* Content */}
                          <div className="flex-1 bg-white dark:bg-gray-800/40 rounded-xl p-3.5 border border-gray-100 dark:border-gray-700/60 shadow-sm hover:border-green-200 dark:hover:border-green-900/50 hover:shadow-md transition-all duration-300">
                            <div className="flex flex-wrap items-center justify-between gap-y-1 gap-x-3 mb-1.5">
                              <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                                {lm.name}
                              </span>
                              {lm.km_mark != null && (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-md border border-emerald-100/50 dark:border-emerald-800/30 shrink-0">
                                  <Milestone className="w-3.5 h-3.5" />
                                  At {lm.km_mark} km
                                </span>
                              )}
                            </div>
                            {lm.description && (
                              <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                                {lm.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Right column — Sidebar */}
          <div className="space-y-6">
            {/* Quick Details card */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md border border-gray-100 dark:border-gray-800"
            >
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-5 uppercase tracking-wider">
                Event Details
              </h3>

              <div className="space-y-4">
                <InfoItem
                  icon={Calendar}
                  label="Start Date"
                  value={formatDate(event.start_date)}
                  iconColor="text-orange-500"
                />
                <InfoItem
                  icon={Clock}
                  label="Start Time"
                  value={formatTime(event.start_date)}
                  iconColor="text-orange-500"
                />
                <InfoItem
                  icon={Calendar}
                  label="End Date"
                  value={formatDate(event.end_date)}
                  iconColor="text-red-500"
                />
                {locationStr && (
                  <InfoItem
                    icon={MapPin}
                    label="Location"
                    value={locationStr}
                    iconColor="text-green-500"
                  />
                )}
                <InfoItem
                  icon={Users}
                  label="Registrations"
                  value={`${event.registration_count ?? 0}${event.capacity === 0 ? " / Unlimited" : (event.capacity > 0 ? ` / ${event.capacity}` : "")} participants`}
                  iconColor="text-blue-500"
                />
                <InfoItem
                  icon={IndianRupee}
                  label="Entry Fee"
                  value={event.is_paid ? `₹${event.entry_fee}` : "Free Event"}
                  iconColor="text-yellow-500"
                />
              </div>

              {/* Capacity progress */}
              {event.capacity > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span>Spots filled</span>
                    <span>
                      {event.registration_count ?? 0} / {event.capacity}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${Math.min(
                          ((event.registration_count ?? 0) / event.capacity) * 100,
                          100
                        )}%`,
                      }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-full rounded-full ${
                        (event.registration_count ?? 0) >= event.capacity
                          ? "bg-red-500"
                          : (event.registration_count ?? 0) / event.capacity > 0.8
                          ? "bg-amber-500"
                          : "bg-green-500"
                      }`}
                    />
                  </div>
                  {(event.registration_count ?? 0) >= event.capacity && (
                    <p className="text-xs text-red-500 mt-1 font-medium">
                      Event is full
                    </p>
                  )}
                </div>
              )}
            </motion.div>

            {/* Register CTA */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="bg-gradient-to-br from-green-600 to-emerald-700 dark:from-green-700 dark:to-emerald-800 rounded-2xl p-6 shadow-lg text-white"
            >
              <h3 className="text-lg font-bold mb-2">Ready to ride?</h3>
              <p className="text-sm text-green-100/80 mb-5">
                Secure your spot and join fellow cyclists for this amazing event.
              </p>
              <button
                onClick={() => setShowRegModal(true)}
                disabled={event.capacity > 0 && (event.registration_count ?? 0) >= event.capacity}
                className="w-full py-3 rounded-xl bg-white text-green-700 font-bold text-sm hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {event.capacity > 0 && (event.registration_count ?? 0) >= event.capacity
                  ? "Event Full"
                  : "Register Now"}
              </button>
            </motion.div>

            {/* Organizer card */}
            {event.organizer_id && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md border border-gray-100 dark:border-gray-800"
              >
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 uppercase tracking-wider">
                  Organized by
                </h3>
                <div className="flex items-center gap-3">
                  {event.organizer_id.logo_url ? (
                    <img
                      src={getImageUrl(event.organizer_id.logo_url)}
                      alt={event.organizer_id.organization_name}
                      className="w-12 h-12 rounded-xl object-cover border border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {event.organizer_id.organization_name}
                    </p>
                    {event.organizer_id.city && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {event.organizer_id.city}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* ── Registration Modal ── */}
      <Modal
        open={showRegModal}
        onClose={() => setShowRegModal(false)}
        title={`Register for ${event.title}`}
      >
        <EventRegistrationForm
          event={event}
          onClose={() => setShowRegModal(false)}
        />
      </Modal>
    </div>
  );
};

export default EventDetailPage;
